import { useState, type FormEvent } from 'react';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/TextField';
import { authClient } from '../auth/authClient';
import { COPY, describeAuthError, validatePassword } from '../auth/authErrors';

export function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    const weak = validatePassword(next);
    if (weak) return setError(weak);
    if (next !== confirm) return setError(COPY.mismatch);
    setError(null);
    setBusy(true);
    try {
      // The Worker always signs out the other devices (it forces revokeOtherSessions), so no flag is sent.
      const { error: err } = await authClient.changePassword({
        currentPassword: current,
        newPassword: next,
      });
      if (err) return setError(describeAuthError(err, 'change-password'));
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch {
      setError(COPY.offlineTryAgain);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      aria-label="Change password"
      onSubmit={onSubmit}
      noValidate
      className="flex flex-col gap-3"
    >
      <TextField
        label="Current password"
        type="password"
        autoComplete="current-password"
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
      />
      <TextField
        label="New password"
        type="password"
        autoComplete="new-password"
        hint="At least 10 characters."
        value={next}
        onChange={(e) => setNext(e.target.value)}
      />
      <TextField
        label="Type the new password again"
        type="password"
        autoComplete="new-password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {done ? (
        <p role="status" className="text-sm text-success">
          {COPY.passwordChanged}
        </p>
      ) : null}
      <p className="text-xs text-muted">Changing your password signs out your other devices.</p>
      <Button type="submit" disabled={busy}>
        Change password
      </Button>
    </form>
  );
}
